const {
  empty,
  email,
  phoneNumber,
  contactName,
  networkCode,
  totalWeight,
  shippingRef,
  parcelDescription,
  customsValue,
  numberOfParcels,
  deliveryInstructions,
  currency,
  deliveryDescription,
  destinationTaxId,
  gstVatPaid,
  town,
  postcode,
  dropAtShop,
} = require("../shared");
const {
  composeDecorators,
  createModel,
  getFieldOptions,
  messages,
  withLabels,
} = require("../utils");
const { M, COUNTRY_CODE } = require("../constants");
const { Joi, modelOptions } = require("../joi");
const { parcels } = require("./parcel");
const { updateParcelProductMessageSuffix } = require("./product");
const { address } = require("./address");

const contactDetails = createModel((_, options) => {
  const fieldOptions = getFieldOptions(options);

  return {
    contactName: contactName()
      .allow(empty)
      .options(fieldOptions),
    telephone: phoneNumber()
      .allow(empty)
      .options(fieldOptions),
  };
});

const notificationDetails = createModel((_, options) => {
  const fieldOptions = getFieldOptions(options);

  return {
    email: email()
      .allow(empty)
      .options(fieldOptions),
    mobile: phoneNumber()
      .allow(empty)
      .options(fieldOptions),
  };
});

const collectionContactDetails = createModel((_, options) => {
  const fieldOptions = getFieldOptions(options);

  return {
    contactName: contactName()
      .allow(...empty)
      .options(fieldOptions),
    telephone: Joi.string()
      .max(15)
      .example("+4402012341234")
      .allow(...empty)
      .options(fieldOptions),
    email: email()
      .allow(...empty)
      .options(fieldOptions),
  };
});

// @see: https://it.dpduk.live/it/diagram/diag_Bxyo3B6C48kIGAcS.html?id=1617292747898
const collectionDetails = createModel((prefix, options) => ({
  address: address(`${prefix}.address`, options)
    .keys(
      withLabels(
        {
          postcode: postcode()
            .required()
            .options(getFieldOptions(options)),
        },
        `${prefix}.address`
      )
    )
    .unknown(),
  contactDetails: collectionContactDetails(`${prefix}.contactDetails`, options),
  dropAtShop: dropAtShop().optional(),
}));

// @see: https://it.dpduk.live/it/diagram/diag_DxNo3B6C48kIGA7G.html?id=1617292904655
const deliveryDetails = createModel((prefix, options) => ({
  address: address(`${prefix}.address`, options)
    .keys(
      withLabels(
        {
          town: town()
            .required()
            .options(getFieldOptions(options)),
        },
        `${prefix}.address`
      )
    )
    .unknown(),
  contactDetails: contactDetails(`${prefix}.contactDetails`),
  notificationDetails: notificationDetails(`${prefix}.notificationDetails`),
}));

const outboundDeliveryDetails = createModel((prefix, options) => ({
  address: address(`${prefix}.address`, options)
    .keys(
      withLabels(
        {
          town: town()
            .required()
            .options(getFieldOptions(options)),
        },
        `${prefix}.address`
      )
    )
    .unknown(),
  contactDetails: contactDetails(`${prefix}.contactDetails`)
    .when(Joi.ref("address.countryCode"), {
      is: Joi.not(COUNTRY_CODE.GREAT_BRITAIN),
      then: contactDetails(`${prefix}.contactDetails`).keys(
        withLabels(
          {
            telephone: phoneNumber()
              .required()
              .options(getFieldOptions(options)),
          },
          `${prefix}.contactDetails`
        )
      ),
    })
    .default({}),
  notificationDetails: notificationDetails(`${prefix}.notificationDetails`),
}));

// @see: https://it.dpduk.live/it/diagram/diag_NIfI3B6C48kIGAIP.html?id=1617292622458
exports.consignment = composeDecorators(
  joiObject => (...params) =>
    joiObject(...params).error(
      messages({
        "any.required": ({ context }) => `Shipment missing ${context.label}`,
        "object.base": ({ context }) => `Shipment missing ${context.label}`,
      })
    ),
  createModel((prefix, options = {}) => {
    const fieldOptions = getFieldOptions(options);

    return {
      collectionDetails: collectionDetails(
        `${prefix}.collectionDetails`,
        options
      ),
      deliveryDetails: deliveryDetails(`${prefix}.deliveryDetails`, options),
      deliveryInstructions: deliveryInstructions()
        .allow(empty)
        .options(fieldOptions),
      liability: Joi.boolean().options(fieldOptions),
      numberOfParcels: numberOfParcels()
        .required()
        .options(fieldOptions),
      parcelDescription: parcelDescription().options(fieldOptions),
      shippingRef1: shippingRef().options(fieldOptions),
      shippingRef2: shippingRef().options(fieldOptions),
      shippingRef3: shippingRef().options(fieldOptions),
      shippingRef4: shippingRef().options(fieldOptions),
      shippingRef6: shippingRef().options(fieldOptions),
      totalWeight: totalWeight()
        .required()
        .options(fieldOptions),
      addressBookId: Joi.string()
        .max(36)
        .allow(empty)
        .options(fieldOptions),
      currency: currency().options(fieldOptions),
      deliveryDescription: deliveryDescription().options(fieldOptions),
      networkCode: networkCode()
        .example("1^48")
        .required()
        .options(fieldOptions),
    };
  })
);

exports.inboundConsignment = exports.consignment;

exports.outboundConsignment = (prefix, options) =>
  exports
    .consignment(prefix, options)
    .keys(
      withLabels(
        {
          networkCode: networkCode()
            .example("1^07")
            .options(getFieldOptions(options)),
          destinationTaxId: destinationTaxId().options(
            getFieldOptions(options)
          ),
          gstVatPaid: gstVatPaid().options(getFieldOptions(options)),
          // NOTE: should validate it only if ums.securitySettings.disableExtendedLiability is false
          // validate later in the validateExtendedLiabilityAndValue middleware
          liabilityValue: Joi.any(),
          customsValue: customsValue().options(getFieldOptions(options)),
          deliveryDescription: deliveryDescription(45).options(
            getFieldOptions(options)
          ),
          deliveryDetails: outboundDeliveryDetails(
            `${prefix}.deliveryDetails`,
            options
          ),
          parcels: parcels(prefix, options)
            .max(Joi.ref("numberOfParcels"))
            .error(errors =>
              updateParcelProductMessageSuffix(
                messages({
                  "array.max": M.PARCELS_ALLOWED_LENGTH,
                })(errors)
              )
            ),
        },
        prefix
      )
    )
    .options(modelOptions);
